import * as React from 'react';
import { VendorViewModel } from '../../../../../_models/Vendor';
import { Errors, Control, Form } from 'react-redux-form';
import { getDefaultErrorMessages, hasValue, isValidCity, hasDateValue, hasOnlyAlphaNumeric } from '@common/validators';
import { hasAlpha, hasNumber, isValidZip, isNumeric, isFederalTaxId } from '../../../../../common/validators';
import { DynamicSelectIdentifier } from '../../../../../common/components/dynamic-select/dynamic-select-types';
import { Select, CheckboxInline } from '@common/components';
import { dateParser } from '@common/dateParser';
import * as moment from 'moment';
import ReactDatePicker from 'react-datepicker';
import FormNames from '@constants/FormNames.enum';
import { Portal } from 'react-overlays';

export interface AddressNFinancesComponentProperties {
  readonly vendorVM: VendorViewModel;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly onSaveClick: (model: VendorViewModel) => void;
  readonly isEdit?: boolean;
}
export class AddressNFinancesComponent extends React.Component<AddressNFinancesComponentProperties> {
  render() {
    return (
      <div>
        {!this.props.vendorVM.id && <h2>Step 2 - Vendor Profile - Address & Finances</h2>}
        <Form
          model={FormNames.VendorAddressNFinances}
          validateOn={'change'}
          hideNativeErrors={true}
          onSubmit={this.props.onSaveClick}
        >
          <div className="row middle-xs">
            <div className="form-control col-md-6">
              <label htmlFor=".address.line1">Office Address</label>
              <Control.text
                model=".address.line1"
                maxLength={32}
                validators={{ required: hasValue, hasAlpha, hasNumber }}
              />
              <Errors
                className="error error-pad"
                model=".address.line1"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-6">
              <label htmlFor=".address.line2">Address 2</label>
              <Control.text model=".address.line2" maxLength={32} />
              <Errors
                className="error error-pad"
                model=".address.line2"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row middle-xs">
            <div className="form-control col-xs-3">
              <label htmlFor=".address.city">City</label>
              <Control.text model=".address.city" maxLength={28} validators={{ required: hasValue, isValidCity }} />
              <Errors
                className="error error-pad"
                model=".address.city"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>

            <div className="form-control col-xs-2">
              <label htmlFor=".address.state.abbreviation">State</label>
              <Select
                identifier={DynamicSelectIdentifier.USStates}
                validators={{ required: hasValue }}
                model=".address.state.abbreviation"
              />
              <Errors
                className="error error-pad"
                model=".address.state.abbreviation"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>

            <div className="form-control col-xs-2">
              <label htmlFor=".address.zip">Zip</label>
              <Control.text model=".address.zip" maxLength={5} validators={{ required: hasValue, isValidZip }} />
              <Errors
                className="error error-pad"
                model=".address.zip"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-5">
              <label htmlFor=".apName">A/P Name</label>
              <Control.text model=".apName" maxLength={64} validators={{ hasAlpha }} />
              <Errors
                className="error error-pad"
                model=".apName"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row middle-xs">
            <div className="form-control col-xs-3">
              <label htmlFor=".parentId">Parent</label>
              <Control.text model=".parentId" validators={{ isNumeric }} />
              <Errors
                className="error error-pad"
                model=".parentId"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-2">
              <label htmlFor=".vendorType.id">Vendor Type</label>
              <Select
                identifier={DynamicSelectIdentifier.VendorTypes}
                validators={{ required: hasValue }}
                model=".vendorType.id"
              />
              <Errors
                className="error error-pad"
                model=".vendorType.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>

            {this.props.isEdit && (
              <div className="form-control col-xs-2">
                <label htmlFor=".vendorStatus.id">Vendor Status</label>
                <Select
                  identifier={DynamicSelectIdentifier.VendorStatuses}
                  // validators={{ required: hasValue }}
                  model=".vendorStatus.id"
                  disabled={!this.props.vendorVM.converted}
                />
                <Errors
                  className="error error-pad"
                  model=".vendorStatus.id"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
            )}

            <div className="form-control col-xs-2">
              <label htmlFor=".licenseNumber">License</label>
              <Control.text
                model=".licenseNumber"
                validators={{ required: hasValue, hasOnlyAlphaNumeric }}
                maxLength={14}
              />
              <Errors
                className="error error-pad"
                model=".licenseNumber"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-3">
              <label htmlFor=".licenseExpiration">License Expires</label>
              <Control
                model=".licenseExpiration"
                validators={{ required: hasDateValue }}
                component={props => {
                  let val;
                  if (props.value && !moment.isMoment(props.value)) {
                    val = dateParser.parse(props.value);
                  } else {
                    // already a moment
                    val = props.value;
                  }

                  return (
                    <ReactDatePicker
                      onChange={props.onChange}
                      popperContainer={({ children }) => {
                        const el = document.getElementById('calendar-portal');

                        return <Portal container={el}>{children}</Portal>;
                      }}
                      value={val ? val.format('MM/DD/YYYY') : ''}
                      selected={val || null}
                    />
                  );
                }}
              />
              <Errors
                className="error error-pad"
                model=".licenseExpiration"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row middle-xs">
            <div className="form-control col-xs-3">
              <label htmlFor=".taxId">Federal Tax ID</label>
              <Control.text model=".taxId" maxLength={12} validators={{ required: hasValue, isFederalTaxId }} />
              <Errors
                className="error error-pad"
                model=".taxId"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="formcontrol col-xs-1">
              <CheckboxInline>
                <Control.checkbox model=".has1099" />
                <label htmlFor=".has1099">1099?</label>
              </CheckboxInline>
            </div>
            <div className="form-control col-xs-3">
              <label htmlFor=".w9Status.id">W9</label>
              <Select
                identifier={DynamicSelectIdentifier.VendorW9Statuses}
                validators={{ required: hasValue }}
                model=".w9Status.id"
              />
              <Errors
                className="error error-pad"
                model=".w9Status.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-3">
              <label htmlFor=".w9SignedDate">W9 Signed Date</label>
              <Control
                model=".w9SignedDate"
                validators={{ required: hasDateValue }}
                component={props => {
                  let val;
                  if (props.value && !moment.isMoment(props.value)) {
                    val = dateParser.parse(props.value);
                  } else {
                    // already a moment
                    val = props.value;
                  }

                  return (
                    <ReactDatePicker
                      onChange={props.onChange}
                      popperContainer={({ children }) => {
                        const el = document.getElementById('calendar-portal');

                        return <Portal container={el}>{children}</Portal>;
                      }}
                      value={val ? val.format('MM/DD/YYYY') : ''}
                      selected={val || null}
                    />
                  );
                }}
              />
              <Errors
                className="error error-pad"
                model=".w9SignedDate"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-xs-2">
              <label htmlFor=".w9TaxYear">W9 Tax Year</label>
              <Control.text model=".w9TaxYear" maxLength={4} validators={{ required: hasValue, isNumeric }} />
              <Errors
                className="error error-pad"
                model=".w9TaxYear"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="col-md-1">
            <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
              Save
            </button>
          </div>
        </Form>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/vendor/_components/flows/steps/address-n-finances/address-n-finances.component.tsx